import { buildFileDropRegionHandlers } from '~/features/files/file-drop-region';
import { isDesktopFileDrag } from '~/lib/file-drag';
import { useProBuilderUploadStore } from './pro-builder-upload-store';

/**
 * FRONT-3078: the one drop handler behind both drop targets (the terminal area and the dialog's own
 * area). Only a real OS file drag arms it (an in-page card drag has no `Files` type); a drop queues the
 * files for the BOUND folder, and `openDialog` also opens the dialog, so the person watches the rows
 * there (the terminal area passes it; the dialog is already open and leaves it out). With no bound folder
 * it is off.
 */
export function proBuilderDropHandlers(params: {
  folder: string | null;
  depth: { current: number };
  setOver: (over: boolean) => void;
  openDialog?: boolean;
}) {
  const { folder, depth, setOver, openDialog = false } = params;
  return buildFileDropRegionHandlers({
    enabled: folder !== null,
    depth,
    setOver,
    onFiles: (files) => {
      if (!folder || !files || files.length === 0) return;
      const store = useProBuilderUploadStore.getState();
      store.enqueue(Array.from(files), folder);
      if (openDialog) store.openDialog();
    },
  });
}

/**
 * A drag that ended without a drop reaching the zone (dropped outside, cancelled with Escape, or the
 * terminal's renderer replaced the row under the pointer so a `dragleave` never came) must not leave the
 * overlay up: forget the depth and hide it.
 */
export function resetDropZone(depth: { current: number }, setOver: (over: boolean) => void): void {
  depth.current = 0;
  setOver(false);
}


/**
 * While the upload dialog is open, a file released on the dimmed backdrop (outside the dialog box) must not
 * open in the tab and kill every queued upload. For an OS file drag only: `dragover` and `drop` are
 * cancelled, and a `drop` that no inner target handled (`defaultPrevented` is false: the box's own handler
 * cancels it) is queued exactly like a drop on the box. An in-page drag is untouched.
 */
export function windowFileDragDecision(e: {
  type: string;
  defaultPrevented: boolean;
  dataTransfer?: { types?: readonly string[] | null } | null;
}): { prevent: boolean; enqueue: boolean } {
  if (!isDesktopFileDrag(e.dataTransfer?.types)) return { prevent: false, enqueue: false };
  return { prevent: true, enqueue: e.type === 'drop' && !e.defaultPrevented };
}

/** The window listener body for the open upload dialog: applies `windowFileDragDecision` to a real event. */
export function handleWindowFileDrag(
  e: { type: string; defaultPrevented: boolean; dataTransfer?: { types?: readonly string[] | null; files?: FileList | null } | null; preventDefault: () => void },
  folder: string | null,
  enqueue: (files: File[], folder: string) => void,
): void {
  const d = windowFileDragDecision(e);
  if (d.prevent) e.preventDefault();
  const files = e.dataTransfer?.files;
  if (d.enqueue && folder && files && files.length > 0) enqueue(Array.from(files), folder);
}
